import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["option", "radio", "radioSelected", "previousButton", "nextButton", "progressBar", "progressText", "questionContainer", "questionCard"]

  connect() {
    this.currentQuestionId = this.element.dataset.questionId
    this.answers = new Map()
    this.isProcessing = false // Prevent multiple simultaneous requests
    this.assessmentType = this.element.dataset.assessmentType || 'right_role' // default to right_role
    
    // Get assessment data for last question detection
    this.currentQuestionIndex = parseInt(this.element.dataset.currentQuestionIndex) || 0
    this.totalQuestions = parseInt(this.element.dataset.totalQuestions) || 0
    this.isLastQuestion = this.element.dataset.isLastQuestion === 'true'
    
    this.updatePreviousButton()
    this.updateNextButton()

    if (this.assessmentType === 'position') {
      this.updateProgress()
    }
  }

  selectAnswer(event) {
    // Prevent default behavior and multiple clicks
    event.preventDefault()
    event.stopPropagation()
    if (this.isProcessing) {
      console.log("Already processing selection, ignoring click")
      return
    }

    const option = event.currentTarget
    const score = option.dataset.score

    // Set processing flag
    this.isProcessing = true

    // Handle different assessment types
    if (this.assessmentType === 'position') {
      // Position Assessment: Handle numbered score buttons
      this.element.querySelectorAll('.score-option').forEach(btn => {
        btn.classList.remove('selected', 'border-orange-500', 'bg-orange-100')
        btn.classList.add('border-gray-300', 'bg-white')
      })
      // Add selection to clicked option
      option.classList.add('selected', 'border-orange-500', 'bg-orange-100')
      option.classList.remove('border-gray-300', 'bg-white')
    } else {
      // Right Role Assessment: Handle radio button options
      this.optionTargets.forEach(opt => {
        // Remove old classes
        opt.classList.remove('border-primary', 'bg-gray-100')
        // Remove selected state styles
        opt.classList.remove('ring-4', 'ring-purple-200', 'border-purple-400', 'bg-purple-50', 'transform', 'scale-105')
        opt.querySelector('[data-question-target="radioSelected"]').classList.add('hidden')

        // Reset radio button border
        const radio = opt.querySelector('[data-question-target="radio"]')
        radio.classList.remove('border-purple-500', 'bg-purple-50')
        radio.classList.add('border-gray-300')
      })

      // Add selected state to clicked option
      option.classList.add('ring-4', 'ring-purple-200', 'border-purple-400', 'bg-purple-50', 'transform', 'scale-105')
      option.querySelector('[data-question-target="radioSelected"]').classList.remove('hidden')
      // Update radio button styling
      const selectedRadio = option.querySelector('[data-question-target="radio"]')
      selectedRadio.classList.remove('border-gray-300')
      selectedRadio.classList.add('border-purple-500', 'bg-purple-50')
    }

    // Store the answer
    this.answers.set(this.currentQuestionId, score)

    // Add visual feedback
    option.classList.add('animate-pulse')

    // Update next button state
    this.updateNextButton()

    // Check if this might be the last question and show loading immediately
    if (this.isLastQuestion) {
      // Show loading overlay immediately for last question
      setTimeout(() => {
        this.showLoadingOverlay()
      }, 100) // Small delay to show the selection animation first
    }

    // Small delay for visual feedback, then advance (Safari-compatible)
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.nextQuestion()
      }, 300) // Shorter delay for better Safari compatibility
    })
  }

  updatePreviousButton() {
    // With randomized order, we always show the previous button
    // The server will handle whether there's actually a previous question
    this.previousButtonTarget.disabled = false
  }

  updateNextButton() {
    // Enable next button if an answer is selected
    if (this.hasNextButtonTarget) {
      let hasSelection = false
      if (this.assessmentType === 'position') {
        // For position assessment, check if any score button is selected
        hasSelection = this.element.querySelector('.score-option.selected') !== null
      } else {
        // For right role assessment, check if answer is stored
        hasSelection = this.answers.has(this.currentQuestionId)
      }

      this.nextButtonTarget.disabled = !hasSelection
    }
  }

  updateProgress() {
    if (this.assessmentType === 'position') {
      // Get progress from server data attributes
      const progressPercentage = parseFloat(this.element.dataset.progressPercentage) || 0

      if (this.hasProgressBarTarget) {
        this.progressBarTarget.style.width = `${progressPercentage}%`
      }

      if (this.hasProgressTextTarget) {
        this.progressTextTarget.textContent = `${Math.round(progressPercentage)}%`
      }
    }
  }

  scrollQuestionToTop(parent) {
    if (!parent) return
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" })
      const newContainer = parent.firstElementChild
      const scrollable = newContainer?.querySelector(".overflow-y-auto")
      if (scrollable) scrollable.scrollTo({ top: 0, behavior: "smooth" })
    })
  }

  async previousQuestion() {
    if (this.isProcessing) return

    this.isProcessing = true
    try {
      let url
      if (this.assessmentType === 'position') {
        const jobListingId = this.element.dataset.jobListingId
        url = `/job_listings/${jobListingId}/position_assessment/previous_question?question_id=${this.currentQuestionId}`
      } else {
        url = `/right_role_assessment/previous_question?question_id=${this.currentQuestionId}`
      }
      const response = await fetch(url, {
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (response.ok) {
        const html = await response.text()
        const parent = this.element.parentElement
        this.element.outerHTML = html
        this.scrollQuestionToTop(parent)
      } else {
        console.error("Failed to load previous question:", response.status)
        this.isProcessing = false
      }
    } catch (error) {
      console.error("Error loading previous question:", error)
      this.isProcessing = false
    }
  }

  async nextQuestion() {
    if (!this.answers.has(this.currentQuestionId)) {
      this.isProcessing = false
      return
    }

    // Check if this might be the last question
    if (this.isLastQuestion) {
      this.showLoadingOverlay()
    }

    try {
      let url, body
      if (this.assessmentType === 'position') {
        const jobListingId = this.element.dataset.jobListingId
        url = `/job_listings/${jobListingId}/position_assessment/next_question`
        body = new FormData()
        body.append('question_id', this.currentQuestionId)
        body.append('score', this.answers.get(this.currentQuestionId))
      } else {
        url = `/right_role_assessment/next_question`
        body = JSON.stringify({
          question_id: this.currentQuestionId,
          score: this.answers.get(this.currentQuestionId)
        })
      }

      const headers = {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
      }

      if (this.assessmentType !== 'position') {
        headers["Content-Type"] = "application/json"
      }

      const response = await fetch(url, {
        method: "POST",
        headers: headers,
        body: body,
        redirect: 'manual' // Handle redirects manually
      })

      if (response.ok) {
        // Check if response is HTML (next question) or JSON (error)
        const contentType = response.headers.get('content-type')
        if (contentType && contentType.includes('text/html')) {
          const html = await response.text()
          const parent = this.element.parentElement
          this.element.outerHTML = html
          this.scrollQuestionToTop(parent)
        } else {
          // Handle JSON response (might be an error)
          await response.json()
          this.isProcessing = false
          this.hideLoadingOverlay()
        }
      } else if (response.status === 0 || response.type === 'opaqueredirect') {
        // Assessment complete, redirect to results page with confetti parameter
        window.location.href = '/right_role_assessment?confetti=true'
        return
      } else {
        console.error("Failed to advance to next question:", response.status)
        this.isProcessing = false
        this.hideLoadingOverlay()
      }
    } catch (error) {
      console.error("Error advancing to next question:", error)
      this.isProcessing = false
      this.hideLoadingOverlay()
    }
  }

  showLoadingOverlay() {
    const overlay = document.getElementById('loading-overlay')
    const content = document.getElementById('loading-content')
    if (overlay && content) {
      overlay.classList.remove('hidden')
      // Trigger animation after a brief delay to ensure the element is visible
      setTimeout(() => {
        content.classList.remove('scale-95', 'opacity-0')
        content.classList.add('scale-100', 'opacity-100')
      }, 10)
    }
  }

  hideLoadingOverlay() {
    const overlay = document.getElementById('loading-overlay')
    const content = document.getElementById('loading-content')
    if (overlay && content) {
      content.classList.remove('scale-100', 'opacity-100')
      content.classList.add('scale-95', 'opacity-0')
      // Hide overlay after animation completes
      setTimeout(() => {
        overlay.classList.add('hidden')
      }, 300)
    }
  }
}
